// src/features/tasks/components/KanbanBoard/index.jsx
import React, {
  useEffect, useMemo, useRef, useState, useCallback,
} from 'react';
import { Link }             from 'react-router-dom';
import { useReactToPrint }  from 'react-to-print';
import { useAuth }          from '../../../auth/context/AuthContext';
import LoadingSpinner       from '../../../../shared/components/ui/LoadingSpinner';
import EmptyState           from '../../../../shared/components/ui/EmptyState';
import TaskAttachmentModal  from '../TaskAttachmentModal';
import PrintableTasks       from '../../../../shared/components/print/PrintableTasks';
import api                  from '../../../../api/axiosInstance';
import { fetchUsers, fetchSettings, fetchTasks } from '../../../../api';
import {
  formatDate, isDateLate,
  formatDateHeader as fmtHeaderFromLib,
} from '../../../../shared/utils/formatters';
import styles from './KanbanBoard.module.css';

// ─── ثوابت ──────────────────────────────────────────────────
const TABS = [
  { id: 'active',    label: 'النشطة'        },
  { id: 'todo',      label: 'مطلوبة'        },
  { id: 'progress',  label: 'قيد التنفيذ'   },
  { id: 'done',      label: 'مكتملة'        },
  { id: 'cancelled', label: 'ملغاة'         },
  { id: 'all',       label: 'الكل'          },
];

const STATUS_META = {
  todo:        { label: 'مطلوبة',      badgeCls: 'badgeTodo',      cardCls: 'cardTodo'      },
  in_progress: { label: 'قيد التنفيذ', badgeCls: 'badgeProgress',  cardCls: 'cardProgress'  },
  done:        { label: 'مكتملة',      badgeCls: 'badgeDone',      cardCls: 'cardDone'      },
  cancelled:   { label: 'ملغاة',       badgeCls: 'badgeCancelled', cardCls: 'cardCancelled' },
};

const PRIORITY_META = {
  high:   { label: 'أولوية عالية', badgeCls: 'badgeHigh'   },
  medium: { label: 'متوسطة',       badgeCls: 'badgeMedium' },
  low:    { label: 'منخفضة',       badgeCls: 'badgeLow'    },
};

const USER_COLORS = [
  { bg: '#E6F1FB', fg: '#185FA5', bd: '#B5D4F4' },
  { bg: '#E1F5EE', fg: '#085041', bd: '#9FE1CB' },
  { bg: '#EEEDFE', fg: '#534AB7', bd: '#CECBF6' },
  { bg: '#FAEEDA', fg: '#854F0B', bd: '#FAC775' },
  { bg: '#FAECE7', fg: '#712B13', bd: '#F5C4B3' },
  { bg: '#FBEAF0', fg: '#72243E', bd: '#F4C0D1' },
];

const isStaffUser = (u) => {
  const r = (u.role_key || u.role || '').toLowerCase();
  return r === 'admin' || r === 'dept_manager' || r === 'employee' ||
         r === 'مدير'  || r === 'مدير قسم'     || r === 'موظف';
};

const isLate        = (due, status) => isDateLate(due, status);
const fmtDueLabel   = (due) => { if (!due) return null; const h = fmtHeaderFromLib(due); return { text: h.text, late: h.isLate }; };
const fmtDateHeader = (dk)  => { if (!dk || dk === 'nodate') return 'بدون تاريخ استحقاق'; return fmtHeaderFromLib(dk).text; };
const getAssigneeName = (u) => { if (!u) return 'غير مسندة'; return `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username; };
const getInitials     = (u) => { if (!u) return '؟'; const n = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username || '؟'; return n.slice(0, 2); };

const applyTabFilter = (tasks, tab) => {
  if (tab === 'active')    return tasks.filter(t => t.status === 'todo' || t.status === 'in_progress');
  if (tab === 'todo')      return tasks.filter(t => t.status === 'todo');
  if (tab === 'progress')  return tasks.filter(t => t.status === 'in_progress');
  if (tab === 'done')      return tasks.filter(t => t.status === 'done');
  if (tab === 'cancelled') return tasks.filter(t => t.status === 'cancelled');
  return tasks;
};

const groupByDate = (tasks) => {
  const g = {};
  tasks.forEach(t => { const k = t.due_date || 'nodate'; if (!g[k]) g[k] = []; g[k].push(t); });
  return Object.entries(g).sort(([a], [b]) => {
    if (a === 'nodate') return 1; if (b === 'nodate') return -1;
    return a < b ? -1 : a > b ? 1 : 0;
  });
};

const CalIcon = () => (
  <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
    <rect x="1" y="3" width="14" height="12" rx="2" stroke="currentColor" strokeWidth="1.2"/>
    <path d="M5 1v4M11 1v4M1 7h14" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
);

// ─── أفاتار المستخدم ────────────────────────────────────────
function UserAvatar({ user, colorScheme, size = 28 }) {
  const [imgError, setImgError] = useState(false);
  const c = colorScheme || USER_COLORS[0];
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      overflow: 'hidden', border: `1.5px solid ${c.bd}`,
      background: c.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {user?.avatar && !imgError ? (
        <img src={user.avatar} alt={getAssigneeName(user)}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onError={() => setImgError(true)} />
      ) : (
        <span style={{ fontSize: size * 0.36, fontWeight: 500, color: c.fg, lineHeight: 1 }}>
          {getInitials(user)}
        </span>
      )}
    </div>
  );
}

// ─── Pie Chart ──────────────────────────────────────────────
function PieChart({ todo, prog, done, cancelled }) {
  const total = todo + prog + done + cancelled;
  if (total === 0) return (
    <svg width="80" height="80" viewBox="0 0 80 80">
      <circle cx="40" cy="40" r="30" fill="none" stroke="#D3D1C7" strokeWidth="10"/>
      <text x="40" y="44" textAnchor="middle" fontSize="12" fill="var(--color-text-secondary)">0</text>
    </svg>
  );
  const doneP  = Math.round(done / total * 100);
  const COLORS = ['#D3D1C7','#85B7EB','#97C459','#F09595'];
  const vals   = [todo, prog, done, cancelled];
  const r = 30, cx = 40, cy = 40, circ = 2 * Math.PI * r;
  let cumulative = 0;
  const segments = vals.map((v, i) => {
    const pct = v / total; const offset = circ * (1 - cumulative); const dash = circ * pct;
    cumulative += pct;
    return { color: COLORS[i], dash, offset, v };
  }).filter(s => s.v > 0);
  return (
    <svg width="80" height="80" viewBox="0 0 80 80" style={{ transform: 'rotate(-90deg)' }}>
      {segments.map((s, i) => (
        <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={s.color} strokeWidth="10"
          strokeDasharray={`${s.dash} ${circ - s.dash}`}
          strokeDashoffset={-s.offset + circ} />
      ))}
      <g style={{ transform: 'rotate(90deg)', transformOrigin: '40px 40px' }}>
        <text x="40" y="37" textAnchor="middle" fontSize="14" fontWeight="500" fill="var(--color-text-primary)">{doneP}%</text>
        <text x="40" y="49" textAnchor="middle" fontSize="8" fill="var(--color-text-secondary)">مكتملة</text>
      </g>
    </svg>
  );
}

function UserStatCard({ user, tasks, colorScheme }) {
  const todo = tasks.filter(t => t.status === 'todo').length;
  const prog = tasks.filter(t => t.status === 'in_progress').length;
  const done = tasks.filter(t => t.status === 'done').length;
  const cancelled = tasks.filter(t => t.status === 'cancelled').length;
  return (
    <div style={{ background: 'var(--bg-base)', border: '0.5px solid var(--border-default)', borderRadius: '12px', padding: '14px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <UserAvatar user={user} colorScheme={colorScheme} size={32} />
        <span style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-primary)' }}>{getAssigneeName(user) || user.username}</span>
      </div>
      <PieChart todo={todo} prog={prog} done={done} cancelled={cancelled} />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'center' }}>
        {todo > 0      && <span style={{ fontSize:'10px', padding:'2px 7px', borderRadius:'20px', background:'#F1EFE8', color:'#5F5E5A', fontWeight:500 }}>{todo} مطلوبة</span>}
        {prog > 0      && <span style={{ fontSize:'10px', padding:'2px 7px', borderRadius:'20px', background:'#E6F1FB', color:'#185FA5', fontWeight:500 }}>{prog} تنفيذ</span>}
        {done > 0      && <span style={{ fontSize:'10px', padding:'2px 7px', borderRadius:'20px', background:'#EAF3DE', color:'#3B6D11', fontWeight:500 }}>{done} مكتملة</span>}
        {cancelled > 0 && <span style={{ fontSize:'10px', padding:'2px 7px', borderRadius:'20px', background:'#FCEBEB', color:'#A32D2D', fontWeight:500 }}>{cancelled} ملغاة</span>}
      </div>
      <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{tasks.length} مهمة إجمالاً</span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// نموذج إنشاء مهمة خارجية
// ═══════════════════════════════════════════════════════════
function ExternalTaskForm({ staffUsers, onCreated, onCancel }) {
  const [form, setForm] = useState({
    title: '', description: '', assigned_to_id: '', due_date: '', status: 'todo',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(p => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setError('عنوان المهمة مطلوب.'); return; }
    setSubmitting(true);
    setError('');
    try {
      const payload = {
        title:       form.title,
        description: form.description,
        due_date:    form.due_date || null,
        status:      form.status,
        is_external: true,
      };
      if (form.assigned_to_id) payload.assigned_to_id = form.assigned_to_id;
      await api.post('/tasks/', payload);
      onCreated();
    } catch (err) {
      const data = err.response?.data;
      setError(data ? JSON.stringify(data) : 'حدث خطأ.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      background: 'var(--bg-base)', border: '0.5px solid var(--border-default)',
      borderRadius: 'var(--radius-xl)', padding: '20px', marginBottom: '20px',
      borderTop: '3px solid var(--color-primary)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 'var(--font-semibold)', color: 'var(--text-heading)', margin: 0 }}>
          إضافة مهمة خارجية
        </h3>
        <button type="button" onClick={onCancel}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: 'var(--text-muted)', lineHeight: 1 }}>×</button>
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <input name="title" value={form.title} onChange={handleChange}
            placeholder="عنوان المهمة *" required
            style={{ gridColumn: '1 / -1' }} />
          <textarea name="description" value={form.description} onChange={handleChange}
            placeholder="الوصف / الملاحظات (اختياري)" rows={2}
            style={{ gridColumn: '1 / -1', resize: 'vertical', fontFamily: 'var(--font-family)', fontSize: 'var(--font-size-sm)', padding: '8px 12px', border: '0.5px solid var(--border-default)', borderRadius: 'var(--radius-md)', background: 'var(--bg-base)', color: 'var(--text-primary)' }} />
          <select name="assigned_to_id" value={form.assigned_to_id} onChange={handleChange}>
            <option value="">— غير مسندة —</option>
            {staffUsers.map(u => <option key={u.id} value={u.id}>{getAssigneeName(u) || u.username}</option>)}
          </select>
          <input type="date" name="due_date" value={form.due_date} onChange={handleChange} />
          <select name="status" value={form.status} onChange={handleChange}>
            <option value="todo">مطلوبة</option>
            <option value="in_progress">قيد التنفيذ</option>
          </select>
        </div>

        {error && (
          <p style={{ margin: 0, padding: '8px 12px', background: 'var(--color-danger-subtle)', border: '0.5px solid var(--color-danger-border)', borderRadius: 'var(--radius-md)', color: 'var(--color-danger-text)', fontSize: '12px' }}>{error}</p>
        )}

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onCancel}
            style={{ padding: '7px 16px', fontSize: '12px', borderRadius: 'var(--radius-md)', border: '0.5px solid var(--border-default)', background: 'var(--bg-base)', color: 'var(--text-secondary)', cursor: 'pointer', fontFamily: 'var(--font-family)' }}>
            إلغاء
          </button>
          <button type="submit" disabled={submitting}
            style={{ padding: '7px 16px', fontSize: '12px', borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--color-primary)', color: '#fff', cursor: 'pointer', fontFamily: 'var(--font-family)', fontWeight: 500, opacity: submitting ? 0.7 : 1 }}>
            {submitting ? 'جاري الحفظ...' : '+ إضافة المهمة'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// بطاقة المهمة
// ═══════════════════════════════════════════════════════════
function TaskCard({ task, canChange, canDelete, staffUsers, userColorMap, onUpdate, onDelete, onOpenAttachment }) {
  const sm           = STATUS_META[task.status]     || STATUS_META.todo;
  const pm           = PRIORITY_META[task.priority] || PRIORITY_META.medium;
  const due          = fmtDueLabel(task.due_date);
  const done         = task.status === 'done';
  const assignedUser = task.assigned_to;
  const colorScheme  = assignedUser ? (userColorMap[assignedUser.id] || USER_COLORS[0]) : USER_COLORS[0];

  return (
    <div className={`${styles.taskCard} ${styles[sm.cardCls]}`}>

      {/* شارة مهمة خارجية */}
      {task.is_external && (
        <div style={{ position: 'absolute', top: '8px', left: '8px', fontSize: '9px', padding: '2px 6px', borderRadius: '20px', background: '#EEEDFE', color: '#534AB7', border: '0.5px solid #CECBF6', fontWeight: 500 }}>
          خارجية
        </div>
      )}

      {task.attachment && (
        <button className={styles.attachBtn} type="button"
          onClick={() => onOpenAttachment(task.attachment, task.title)} title="عرض المرفق">📎</button>
      )}

      <div className={styles.cardTop}>
        <input type="checkbox" className={styles.cardCheckbox} checked={done}
          disabled={!canChange || task.status === 'cancelled'}
          onChange={e => onUpdate(task.id, 'status', e.target.checked ? 'done' : 'in_progress')} />
        <span className={`${styles.cardTitle} ${done ? styles.cardTitleDone : ''}`}>{task.title}</span>
      </div>

      <div className={styles.cardBadges}>
        <span className={`${styles.badge} ${styles[sm.badgeCls]}`}>{sm.label}</span>
        <span className={`${styles.badge} ${styles[pm.badgeCls]}`}>{pm.label}</span>
        {task.deliverable_details?.name && (
          <span className={`${styles.badge} ${styles.badgeStage}`}>{task.deliverable_details.name}</span>
        )}
      </div>

      {/* اسم العميل / رابط الحجز */}
      {task.deliverable_details?.booking && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
          <span style={{ flexShrink: 0 }}>👤</span>
          <span style={{ flexShrink: 0 }}>{task.deliverable_details.booking.client?.name ?? 'عميل'}</span>
          <Link
            to={`/booking/${task.deliverable_details.booking.id}`}
            style={{ marginRight: 'auto', color: '#0d9488', fontWeight: 600, textDecoration: 'none', fontSize: '11px', whiteSpace: 'nowrap' }}
            onClick={e => e.stopPropagation()}
          >
            ← الحجز
          </Link>
        </div>
      )}

      {/* سبب الإلغاء كامل */}
      {task.status === 'cancelled' && task.cancellation_reason && (
        <div style={{ padding: '8px 12px', background: 'var(--color-danger-subtle)', border: '0.5px solid var(--color-danger-border)', borderRight: '3px solid var(--color-danger)', borderRadius: 'var(--radius-sm)', fontSize: '12px', color: 'var(--color-danger-text)', lineHeight: '1.6', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          <strong style={{ display: 'block', marginBottom: '3px', fontSize: '11px' }}>سبب الإلغاء:</strong>
          {task.cancellation_reason}
        </div>
      )}

      {/* أزرار الحالة */}
      {canChange && task.status !== 'cancelled' && (
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {task.status === 'todo' && (
            <>
              <button type="button" style={{ flex:1, padding:'4px 8px', fontSize:'11px', borderRadius:'var(--radius-sm)', background:'var(--color-primary)', color:'var(--text-on-primary)', border:'none', cursor:'pointer' }}
                onClick={() => onUpdate(task.id, 'status', 'in_progress')}>▶ ابدأ التنفيذ</button>
              <button type="button" style={{ padding:'4px 8px', fontSize:'11px', borderRadius:'var(--radius-sm)', background:'var(--color-danger-subtle)', color:'var(--color-danger-text)', border:'1px solid var(--color-danger-muted)', cursor:'pointer' }}
                onClick={() => { const r = window.prompt('سبب الإلغاء (اختياري):'); if (r !== null) onUpdate(task.id, 'status', 'cancelled', { cancellation_reason: r }); }}>✕ إلغاء</button>
            </>
          )}
          {task.status === 'in_progress' && (
            <>
              <button type="button" style={{ flex:1, padding:'4px 8px', fontSize:'11px', borderRadius:'var(--radius-sm)', background:'var(--color-success)', color:'#fff', border:'none', cursor:'pointer' }}
                onClick={() => onUpdate(task.id, 'status', 'done')}>✓ إكمال</button>
              <button type="button" style={{ padding:'4px 8px', fontSize:'11px', borderRadius:'var(--radius-sm)', background:'var(--bg-subtle)', color:'var(--text-body)', border:'1px solid var(--border-default)', cursor:'pointer' }}
                onClick={() => onUpdate(task.id, 'status', 'todo')}>↩ إرجاع</button>
              <button type="button" style={{ padding:'4px 8px', fontSize:'11px', borderRadius:'var(--radius-sm)', background:'var(--color-danger-subtle)', color:'var(--color-danger-text)', border:'1px solid var(--color-danger-muted)', cursor:'pointer' }}
                onClick={() => { const r = window.prompt('سبب الإلغاء (اختياري):'); if (r !== null) onUpdate(task.id, 'status', 'cancelled', { cancellation_reason: r }); }}>✕ إلغاء</button>
            </>
          )}
          {task.status === 'done' && (
            <button type="button" style={{ flex:1, padding:'4px 8px', fontSize:'11px', borderRadius:'var(--radius-sm)', background:'var(--bg-subtle)', color:'var(--text-body)', border:'1px solid var(--border-default)', cursor:'pointer' }}
              onClick={() => onUpdate(task.id, 'status', 'in_progress')}>↩ إعادة الفتح</button>
          )}
        </div>
      )}
      {canChange && task.status === 'cancelled' && (
        <button type="button" style={{ width:'100%', padding:'4px 8px', fontSize:'11px', borderRadius:'var(--radius-sm)', background:'var(--color-primary)', color:'var(--text-on-primary)', border:'none', cursor:'pointer' }}
          onClick={() => onUpdate(task.id, 'status', 'todo')}>↩ إعادة الفتح</button>
      )}

      <div className={styles.cardFooter}>
        <UserAvatar user={assignedUser} colorScheme={colorScheme} size={28} />

        {canChange ? (
          <select style={{ flex:1, minWidth:0, maxWidth:'140px', fontSize:'11px', padding:'3px 6px', border:'0.5px solid var(--border-default)', borderRadius:'var(--radius-sm)', background:'var(--bg-base)', color:'var(--text-primary)', fontFamily:'var(--font-family)' }}
            value={task.assigned_to?.id || ''}
            onChange={e => onUpdate(task.id, 'assigned_to_id', e.target.value || null)}>
            <option value="">— غير مسندة —</option>
            {staffUsers.map(u => <option key={u.id} value={u.id}>{getAssigneeName(u)}</option>)}
          </select>
        ) : (
          <span className={styles.assignName}>{getAssigneeName(task.assigned_to)}</span>
        )}

        {due && (
          <span className={`${styles.duePill} ${due.late ? styles.duePillLate : ''}`}>
            <CalIcon />{due.text}
          </span>
        )}

        {canDelete && (
          <button type="button" className={styles.deleteBtn}
            onClick={() => onDelete(task.id, task.title)} title="حذف">×</button>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// المكوّن الرئيسي
// ═══════════════════════════════════════════════════════════
const KanbanBoard = () => {
  const { hasPermission, loggedInUser } = useAuth();
  const printableRef = useRef(null);

  const [allTasks,   setAllTasks]   = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [settings,   setSettings]   = useState(null);
  const [loading,    setLoading]    = useState(true);

  // ✅ وضع العرض: 'booking' | 'external'
  const [viewMode,       setViewMode]       = useState('booking');
  const [activeTab,      setActiveTab]      = useState('active');
  const [searchText,     setSearchText]     = useState('');
  const [filterUser,     setFilterUser]     = useState('');
  const [filterPri,      setFilterPri]      = useState('');
  const [filterStage,    setFilterStage]    = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo,   setFilterDateTo]   = useState('');
  const [attachment,     setAttachment]     = useState(null);
  const [showStats,      setShowStats]      = useState(false);
  const [showExtForm,    setShowExtForm]    = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [tasksRes, settingsRes, usersRes] = await Promise.all([
        fetchTasks(),
        (hasPermission('manage_settings') || hasPermission('view_all_bookings'))
          ? fetchSettings() : Promise.resolve({ data: null }),
        (hasPermission('view_all_tasks') || hasPermission('view_users') || hasPermission('change_own_task_status'))
          ? fetchUsers() : Promise.resolve({ data: [] }),
      ]);
      setAllTasks(tasksRes.data || []);
      if (settingsRes.data) setSettings(settingsRes.data);
      setStaffUsers((usersRes.data || []).filter(isStaffUser));
    } catch (err) { console.error('فشل تحميل المهام:', err); }
    finally { setLoading(false); }
  }, [hasPermission]);

  useEffect(() => { loadData(); }, [loadData]);

  const handlePrint = useReactToPrint({
    contentRef: printableRef,
    documentTitle: `Tasks-${formatDate(new Date())}`,
  });

  const userColorMap = useMemo(() => {
    const map = {};
    staffUsers.forEach((u, i) => { map[u.id] = USER_COLORS[i % USER_COLORS.length]; });
    return map;
  }, [staffUsers]);

  const visibleTasks = useMemo(() => {
    const isEmp = loggedInUser?.is_employee && !loggedInUser?.is_admin && !loggedInUser?.is_dept_manager;
    const base  = isEmp ? allTasks.filter(t => t.assigned_to?.id === loggedInUser.id) : allTasks;
    // فلتر حسب وضع العرض
    if (viewMode === 'external') return base.filter(t => t.is_external);
    return base.filter(t => !t.is_external);
  }, [allTasks, loggedInUser, viewMode]);

  const filtered = useMemo(() => {
    return visibleTasks.filter(t => {
      if (searchText     && !t.title.toLowerCase().includes(searchText.toLowerCase())) return false;
      if (filterUser     && String(t.assigned_to?.id) !== filterUser)                  return false;
      if (filterPri      && t.priority !== filterPri)                                   return false;
      if (filterStage    && t.deliverable_details?.name !== filterStage)               return false;
      if (filterDateFrom && t.due_date && t.due_date < filterDateFrom)                 return false;
      if (filterDateTo   && t.due_date && t.due_date > filterDateTo)                   return false;
      return true;
    });
  }, [visibleTasks, searchText, filterUser, filterPri, filterStage, filterDateFrom, filterDateTo]);

  const tabCounts = useMemo(() => ({
    active:    filtered.filter(t => t.status === 'todo' || t.status === 'in_progress').length,
    todo:      filtered.filter(t => t.status === 'todo').length,
    progress:  filtered.filter(t => t.status === 'in_progress').length,
    done:      filtered.filter(t => t.status === 'done').length,
    cancelled: filtered.filter(t => t.status === 'cancelled').length,
    all:       filtered.length,
  }), [filtered]);

  const stats = useMemo(() => ({
    total: visibleTasks.length,
    done:  visibleTasks.filter(t => t.status === 'done').length,
    prog:  visibleTasks.filter(t => t.status === 'in_progress').length,
    late:  visibleTasks.filter(t => isLate(t.due_date, t.status)).length,
  }), [visibleTasks]);

  // إحصائيات المهام الخارجية
  const externalCount  = useMemo(() => allTasks.filter(t => t.is_external).length, [allTasks]);
  const bookingCount   = useMemo(() => allTasks.filter(t => !t.is_external).length, [allTasks]);

  const tabTasks     = useMemo(() => applyTabFilter(filtered, activeTab), [filtered, activeTab]);
  const groupedDates = useMemo(() => groupByDate(tabTasks), [tabTasks]);

  const stageOptions = useMemo(() => {
    const s = new Set();
    visibleTasks.forEach(t => { if (t.deliverable_details?.name) s.add(t.deliverable_details.name); });
    return [...s];
  }, [visibleTasks]);

  const userStats = useMemo(() => {
    return staffUsers.map(u => ({
      user:  u,
      tasks: visibleTasks.filter(t => t.assigned_to?.id === u.id),
    })).filter(x => x.tasks.length > 0);
  }, [staffUsers, visibleTasks]);

  const handleUpdate = useCallback(async (taskId, field, value, extra = {}) => {
    if (!hasPermission('change_task') && !hasPermission('change_own_task_status')) return;
    try { await api.patch(`/tasks/${taskId}/`, { [field]: value, ...extra }); loadData(); }
    catch (err) { console.error(err); }
  }, [hasPermission, loadData]);

  const handleDelete = useCallback(async (taskId, title) => {
    if (!hasPermission('delete_task')) return;
    if (!window.confirm(`حذف المهمة: "${title}"؟`)) return;
    try { await api.delete(`/tasks/${taskId}/`); loadData(); }
    catch { alert('فشل الحذف.'); }
  }, [hasPermission, loadData]);

  const clearFilters = useCallback(() => {
    setSearchText(''); setFilterUser(''); setFilterPri('');
    setFilterStage(''); setFilterDateFrom(''); setFilterDateTo('');
  }, []);

  const hasActiveFilter = !!(searchText || filterUser || filterPri || filterStage || filterDateFrom || filterDateTo);
  const canChange       = hasPermission('change_task') || hasPermission('change_own_task_status');
  const canDelete       = hasPermission('delete_task');
  const showUserFilter  = hasPermission('view_all_tasks');
  const isAdmin         = loggedInUser?.is_admin || loggedInUser?.is_superuser;

  if (loading) return <LoadingSpinner message="جاري تحميل المهام..." />;

  const pct = (n) => stats.total ? Math.round(n / stats.total * 100) : 0;

  return (
    <div className={styles.container}>

      <div style={{ display: 'none' }}>
        <PrintableTasks ref={printableRef} tasks={tabTasks}
          filters={{ start_date: filterDateFrom, end_date: filterDateTo, filterByUser: filterUser, filterByStatus: activeTab }}
          settings={settings} users={staffUsers} />
      </div>

      {attachment && (
        <TaskAttachmentModal imageUrl={attachment.url} taskTitle={attachment.title} onClose={() => setAttachment(null)} />
      )}

      {/* ══ مبدّل وضع العرض: مهام الحجوزات / مهام خارجية ══ */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', alignItems: 'center' }}>
        <button type="button"
          onClick={() => { setViewMode('booking'); setShowExtForm(false); }}
          style={{
            padding: '7px 18px', borderRadius: '20px', fontSize: '13px', cursor: 'pointer',
            fontFamily: 'var(--font-family)', fontWeight: viewMode === 'booking' ? 500 : 400,
            border: '0.5px solid', transition: 'all .15s',
            borderColor: viewMode === 'booking' ? 'var(--color-primary)' : 'var(--border-default)',
            background:  viewMode === 'booking' ? 'var(--color-primary-subtle)' : 'var(--bg-base)',
            color:       viewMode === 'booking' ? 'var(--color-primary-text)' : 'var(--text-secondary)',
          }}>
          مهام الحجوزات
          <span style={{ marginRight: '6px', fontSize: '11px', padding: '1px 6px', borderRadius: '20px', background: 'var(--bg-subtle)', color: 'var(--text-muted)' }}>{bookingCount}</span>
        </button>

        <button type="button"
          onClick={() => { setViewMode('external'); setShowExtForm(false); }}
          style={{
            padding: '7px 18px', borderRadius: '20px', fontSize: '13px', cursor: 'pointer',
            fontFamily: 'var(--font-family)', fontWeight: viewMode === 'external' ? 500 : 400,
            border: '0.5px solid', transition: 'all .15s',
            borderColor: viewMode === 'external' ? '#534AB7' : 'var(--border-default)',
            background:  viewMode === 'external' ? '#EEEDFE'  : 'var(--bg-base)',
            color:       viewMode === 'external' ? '#3C3489'  : 'var(--text-secondary)',
          }}>
          مهام خارجية
          <span style={{ marginRight: '6px', fontSize: '11px', padding: '1px 6px', borderRadius: '20px', background: 'var(--bg-subtle)', color: 'var(--text-muted)' }}>{externalCount}</span>
        </button>

        {/* زر إضافة مهمة خارجية — للمدير فقط */}
        {isAdmin && viewMode === 'external' && !showExtForm && (
          <button type="button"
            onClick={() => setShowExtForm(true)}
            style={{
              marginRight: 'auto', padding: '7px 16px', borderRadius: '20px', fontSize: '12px',
              cursor: 'pointer', fontFamily: 'var(--font-family)', fontWeight: 500,
              border: 'none', background: 'var(--color-primary)', color: '#fff',
            }}>
            + إضافة مهمة خارجية
          </button>
        )}
      </div>

      {/* ══ نموذج إنشاء مهمة خارجية ══ */}
      {showExtForm && (
        <ExternalTaskForm
          staffUsers={staffUsers}
          onCreated={() => { setShowExtForm(false); loadData(); }}
          onCancel={() => setShowExtForm(false)}
        />
      )}

      {/* ══ إحصائيات ══ */}
      <div className={styles.statsRow}>
        {[
          { num: stats.total, label: 'إجمالي المهام', cls: '',          fill: 'fillTotal', pct: 100 },
          { num: stats.done,  label: 'مكتملة',        cls: 'colorDone', fill: 'fillDone',  pct: pct(stats.done) },
          { num: stats.prog,  label: 'قيد التنفيذ',   cls: 'colorProg', fill: 'fillProg',  pct: pct(stats.prog) },
          { num: stats.late,  label: 'متأخرة',         cls: 'colorLate', fill: 'fillLate',  pct: pct(stats.late) },
        ].map(s => (
          <div key={s.label} className={styles.statPill}>
            <span className={`${styles.statNum} ${s.cls ? styles[s.cls] : ''}`}>{s.num}</span>
            <span className={styles.statLabel}>{s.label}</span>
            <div className={styles.statBar}>
              <div className={`${styles.statFill} ${styles[s.fill]}`} style={{ width: `${s.pct}%` }} />
            </div>
          </div>
        ))}
      </div>

      {/* ══ فلتر الموظفين chips ══ */}
      {showUserFilter && staffUsers.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '11px', fontWeight: 500, color: 'var(--text-muted)', marginBottom: '8px', letterSpacing: '.04em' }}>تصفية حسب الموظف</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
            <button type="button" onClick={() => setFilterUser('')}
              style={{ padding: '5px 14px', borderRadius: '20px', fontSize: '12px', cursor: 'pointer', fontFamily: 'var(--font-family)', border: '0.5px solid', borderColor: filterUser === '' ? '#378ADD' : 'var(--border-default)', background: filterUser === '' ? '#E6F1FB' : 'var(--bg-base)', color: filterUser === '' ? '#0C447C' : 'var(--text-secondary)', fontWeight: filterUser === '' ? 500 : 400 }}>
              الكل
            </button>
            {staffUsers.map((u, i) => {
              const c = USER_COLORS[i % USER_COLORS.length];
              const active = filterUser === String(u.id);
              return (
                <button key={u.id} type="button" onClick={() => setFilterUser(active ? '' : String(u.id))}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 12px 4px 6px', borderRadius: '20px', cursor: 'pointer', fontFamily: 'var(--font-family)', border: '0.5px solid', borderColor: active ? c.bd : 'var(--border-default)', background: active ? c.bg : 'var(--bg-base)' }}>
                  <UserAvatar user={u} colorScheme={c} size={22} />
                  <span style={{ fontSize: '12px', color: active ? c.fg : 'var(--text-primary)', fontWeight: active ? 500 : 400 }}>{getAssigneeName(u) || u.username}</span>
                </button>
              );
            })}
            {userStats.length > 0 && (
              <button type="button" onClick={() => setShowStats(p => !p)}
                style={{ marginRight: 'auto', padding: '5px 12px', borderRadius: '20px', fontSize: '11px', cursor: 'pointer', fontFamily: 'var(--font-family)', border: '0.5px solid var(--color-info-border)', background: showStats ? 'var(--color-info)' : 'var(--color-info-subtle)', color: showStats ? '#fff' : 'var(--color-info-text)', fontWeight: 500 }}>
                {showStats ? '▲ إخفاء الإحصائيات' : '▼ إحصائيات الموظفين'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* ══ مخططات الموظفين ══ */}
      {showStats && userStats.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '10px', marginBottom: '20px', padding: '16px', background: 'var(--bg-subtle)', borderRadius: 'var(--radius-xl)', border: '0.5px solid var(--border-default)' }}>
          {userStats.map(({ user, tasks }, i) => (
            <UserStatCard key={user.id} user={user} tasks={tasks} colorScheme={USER_COLORS[i % USER_COLORS.length]} />
          ))}
        </div>
      )}

      {/* ══ شريط الفلاتر ══ */}
      <div className={styles.filtersBar}>
        <span className={styles.filterLabel}>تصفية</span>
        <input className={styles.filterSearch} placeholder="ابحث عن مهمة..." value={searchText} onChange={e => setSearchText(e.target.value)} />
        <select className={styles.filterSelect} value={filterPri} onChange={e => setFilterPri(e.target.value)}>
          <option value="">كل الأولويات</option>
          <option value="high">عالية</option>
          <option value="medium">متوسطة</option>
          <option value="low">منخفضة</option>
        </select>
        {stageOptions.length > 0 && (
          <select className={styles.filterSelect} value={filterStage} onChange={e => setFilterStage(e.target.value)}>
            <option value="">كل المراحل</option>
            {stageOptions.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        <div className={styles.dateRange}>
          <span className={styles.dateRangeLabel}>من:</span>
          <input type="date" className={styles.filterDate} value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} />
          <span className={styles.dateRangeLabel}>إلى:</span>
          <input type="date" className={styles.filterDate} value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} />
        </div>
        {hasActiveFilter && (
          <button className={styles.clearBtn} type="button" onClick={clearFilters}>مسح الفلاتر</button>
        )}
        <button className={styles.printBtn} type="button" onClick={handlePrint}>طباعة PDF</button>
      </div>

      {/* ══ Tabs ══ */}
      <div className={styles.tabsBar}>
        {TABS.map(tab => (
          <button key={tab.id} type="button"
            className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : ''}`}
            onClick={() => setActiveTab(tab.id)}>
            {tab.label}
            <span className={styles.tabCount}>{tabCounts[tab.id] ?? 0}</span>
          </button>
        ))}
      </div>

      {/* ══ المحتوى ══ */}
      {tabTasks.length === 0 ? (
        <EmptyState icon="📋" title="لا توجد مهام"
          message={hasActiveFilter ? 'لا توجد مهام تطابق الفلاتر — جرب مسح الفلاتر.' : viewMode === 'external' ? 'لا توجد مهام خارجية. اضغط "+ إضافة مهمة خارجية" لإنشاء واحدة.' : 'لا توجد مهام في هذا القسم.'}
          actionLabel={hasActiveFilter ? 'مسح الفلاتر' : undefined}
          onAction={hasActiveFilter ? clearFilters : undefined} />
      ) : (
        groupedDates.map(([dk, dateTasks]) => {
          const late = dk !== 'nodate' && isLate(dk, 'todo');
          return (
            <div key={dk} className={styles.dateGroup}>
              <div className={styles.dateHeader}>
                <span className={`${styles.datePill} ${late ? styles.datePillLate : ''}`}>
                  <CalIcon />{fmtDateHeader(dk)}
                </span>
                <div className={`${styles.dateLine} ${late ? styles.dateLineLate : ''}`} />
                <span className={`${styles.dateCount} ${late ? styles.dateCountLate : ''}`}>{dateTasks.length} مهمة</span>
              </div>
              <div className={styles.cardGrid}>
                {dateTasks.map(task => (
                  <TaskCard key={task.id} task={task} canChange={canChange} canDelete={canDelete}
                    staffUsers={staffUsers} userColorMap={userColorMap}
                    onUpdate={handleUpdate} onDelete={handleDelete}
                    onOpenAttachment={(url, title) => setAttachment({ url, title })} />
                ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default KanbanBoard;